import React, { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Shield,
  Info,
  CheckCircle2,
  FileText,
  User,
  Loader2,
  AlertCircle,
  CreditCard,
  Image as ImageIcon,
  Lock,
  CalendarDays,
  Wallet,
  Mail, // 🆕 أيقونة الإيميل
  Shirt, // 🆕 أيقونة مقاس التيشيرت
} from "lucide-react";
import "../../assets/styles/MilitaryEducation.css";
import { AuthContext } from "../../context/AuthContext";
import api from "../../services/api";
import PopupMessage from "../../components/common/PopupMessage";

export default function MilitaryEducation() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isMale = user?.gender?.toUpperCase() === "MALE";

  // تغيير المسمى بناءً على الجنس زي الإعلان الرسمي
  const courseName = isMale ? "دورة التربية العسكرية" : "دورة التربية الوطنية";

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [militaryRegistrationEndDate, setMilitaryRegistrationEndDate] =
    useState("");
  const [regStatus, setRegStatus] = useState("NOT_REGISTERED");
  const [adminNotes, setAdminNotes] = useState("");

  // 🆕 إضافة الحقول الجديدة للبيانات الأصلية
  const [originalData, setOriginalData] = useState({
    fullName: "",
    nationalIdNumber: "",
    universityEmail: "",
    tShirtSize: "",
    GroupLink: "",
  });

  const [existingImages, setExistingImages] = useState({
    personalPhoto: null,
    nationalIdFront: null,
    nationalIdBack: null,
    universityId: null,
  });

  // 🆕 إضافة الحقول الجديدة لبيانات الفورم
  const [formData, setFormData] = useState({
    fullName: "",
    nationalIdNumber: "",
    universityEmail: "",
    tShirtSize: "",
    personalPhoto: null,
    nationalIdFront: null,
    nationalIdBack: null,
    universityId: null,
  });

  const [groupLink, setGroupLink] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });

  const fetchMilitaryData = useCallback(
    async (isSilent = false) => {
      if (!isSilent) setIsLoadingData(true);
      try {
        const [settingsRes, statusRes] = await Promise.all([
          api
            .get("/Military/settings")
            .catch(() => ({ data: { isMilitaryRegistrationOpen: true } })),
          api.get("/Military/status"),
        ]);

        setIsRegistrationOpen(settingsRes.data.isMilitaryRegistrationOpen);
        if (settingsRes.data.militaryRegistrationEndDate) {
          setMilitaryRegistrationEndDate(
            settingsRes.data.militaryRegistrationEndDate.split("T")[0],
          );
        }

        if (statusRes.data.isRegistered) {
          const { status, adminNotes, data, GroupLink } = statusRes.data;
          setRegStatus(status);
          setAdminNotes(adminNotes);
          setGroupLink(GroupLink || ""); // 🆕 تحميل رابط المجموعة (لو موجود)

          const loadedData = {
            fullName: data.fullName,
            nationalIdNumber: data.nationalIdNumber,
            universityEmail: data.universityEmail || "", // 🆕 تحميل الإيميل
            tShirtSize: data.tShirtSize || "", // 🆕 تحميل المقاس
            GroupLink: data.GroupLink || "", // 🆕 تحميل رابط المجموعة (لو موجود)
          };

          setOriginalData(loadedData);
          setFormData((prev) => ({
            ...prev,
            ...loadedData,
            personalPhoto: null,
            nationalIdFront: null,
            nationalIdBack: null,
            universityId: null,
          }));

          setExistingImages({
            personalPhoto: data.personalPhoto?.secure_url,
            nationalIdFront: data.nationalIdFrontImage?.secure_url,
            nationalIdBack: data.nationalIdBackImage?.secure_url,
            universityId: data.universityIdImage?.secure_url,
          });
        } else {
          if (user?.name) {
            setFormData((prev) => ({ ...prev, fullName: user.name }));
            setOriginalData((prev) => ({ ...prev, fullName: user.name }));
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        if (!isSilent) setIsLoadingData(false);
      }
    },
    [user],
  );

  useEffect(() => {
    fetchMilitaryData();
  }, [fetchMilitaryData]);

  // 🆕 إضافة الحقول الجديدة للمقارنة لمعرفة إذا كان هناك تغييرات أم لا
  const hasChanges =
    formData.fullName !== originalData.fullName ||
    formData.nationalIdNumber !== originalData.nationalIdNumber ||
    formData.universityEmail !== originalData.universityEmail ||
    formData.tShirtSize !== originalData.tShirtSize ||
    formData.personalPhoto !== null ||
    formData.nationalIdFront !== null ||
    formData.nationalIdBack !== null ||
    formData.universityId !== null;

  const guidelines = [
    `تقام الدورة بنظام الحضور الفعلي بمقر الجامعة لمدة 12 يوماً خلال إجازة نهاية العام.`,
    isMale
      ? "الدورة متطلب تخرج أساسي للطلاب الذكور."
      : "الدورة عبارة عن محاضرات تثقيفية فقط كمتطلب تخرج للطالبات.",
    "يجب تسليم الأوراق شخصياً بإدارة رعاية الشباب بمبنى الأنشطة بعد إتمام التسجيل الإلكتروني.",
    "سيتم تحميل رسوم الدورة من خلال مطالبة مالية خاصة بذلك.",
    "لا يُقبل الاعتذار عن الدورة بعد التسجيل نهائياً.",
  ];

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

      if (file.size > maxSizeInBytes) {
        setPopup({
          show: true,
          type: "error",
          message: "حجم الصورة كبير جداً. أقصى حجم مسموح به هو 5 ميجابايت.",
        });
        e.target.value = null;
        return;
      }

      setFormData({ ...formData, [field]: file });
    }
  };

  const handleNationalIdChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 14) {
      setFormData({ ...formData, nationalIdNumber: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // فاليشن الرقم القومي
    if (formData.nationalIdNumber.length !== 14) {
      setPopup({
        show: true,
        type: "error",
        message: "الرقم القومي يجب أن يتكون من 14 رقم بالضبط.",
      });
      return;
    }

    // 🆕 فاليشن الإيميل الجامعي
    if (
      !formData.universityEmail ||
      !formData.universityEmail.toLowerCase().endsWith("@nub.edu.eg")
    ) {
      setPopup({
        show: true,
        type: "error",
        message: "يرجى إدخال إيميل جامعي صحيح ينتهي بـ @nub.edu.eg",
      });
      return;
    }

    // 🆕 فاليشن مقاس التيشيرت
    if (!formData.tShirtSize) {
      setPopup({
        show: true,
        type: "error",
        message: "يرجى اختيار مقاس التيشيرت الخاص بك.",
      });
      return;
    }

    if (regStatus === "NOT_REGISTERED" && !formData.personalPhoto) {
      setPopup({
        show: true,
        type: "error",
        message: "يرجى رفع الصورة الشخصية.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage(null);

    const submitData = new FormData();
    submitData.append("fullName", formData.fullName);
    submitData.append("nationalIdNumber", formData.nationalIdNumber);
    submitData.append("universityEmail", formData.universityEmail); // 🆕
    submitData.append("tShirtSize", formData.tShirtSize); // 🆕

    if (formData.personalPhoto)
      submitData.append("personalPhoto", formData.personalPhoto);
    if (formData.nationalIdFront)
      submitData.append("nationalIdFrontImage", formData.nationalIdFront);
    if (formData.nationalIdBack)
      submitData.append("nationalIdBackImage", formData.nationalIdBack);
    if (formData.universityId)
      submitData.append("universityIdImage", formData.universityId);

    try {
      if (regStatus === "NOT_REGISTERED") {
        await api.post("/Military/register", submitData);
      } else {
        await api.patch("/Military/update", submitData);
      }

      setSubmitMessage({ type: "success", text: "تم حفظ بياناتك بنجاح!" });
      setPopup({
        show: true,
        type: "success",
        message: "تم إرسال طلبك بنجاح، يرجى متابعة حالة الطلب من هنا.",
      });

      await fetchMilitaryData(true);

      setTimeout(() => {
        setSubmitMessage(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 2000);
    } catch (error) {
      let errorMessage = "حدث خطأ أثناء إرسال البيانات.";

      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const firstErrorKey = Object.keys(validationErrors)[0];
        if (firstErrorKey && validationErrors[firstErrorKey].length > 0) {
          errorMessage = validationErrors[firstErrorKey][0];
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setPopup({
        show: true,
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="military-page-wrapper d-flex justify-content-center align-items-center">
        <Loader2 className="spinner-icon text-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="military-page-wrapper" dir="rtl">
      <div
        style={{ marginTop: "50px" }}
        className="container position-relative z-2"
      >
        {/* Header */}
        <div className="d-flex align-items-center mb-5">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-link p-0 ms-3 text-secondary"
            style={{ textDecoration: "none" }}
          >
            <ArrowRight size={28} />
          </button>
          <div className="d-flex align-items-center gap-3">
            <Shield size={36} className="text-primary" />
            <h2
              className="m-0 fw-bold title-gradient"
              style={{ fontSize: "2rem" }}
            >
              حجز {courseName}
            </h2>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            {/* Guidelines Card */}
            <motion.div
              className="info-card glass-card mb-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="d-flex align-items-center gap-2 mb-4 pb-3 border-bottom border-secondary border-opacity-25">
                <Info size={26} className="text-primary" />
                <h4 className="m-0 fw-bold" style={{ color: "#1e293b" }}>
                  تعليمات الإدارة للتسجيل بالدورة
                </h4>
              </div>
              <ul className="guidelines-list mb-4">
                {guidelines.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ x: 10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <CheckCircle2
                      size={20}
                      className="text-success ms-3 flex-shrink-0 mt-1"
                    />
                    <span className="fw-semibold text-secondary">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <div
                className="d-flex flex-wrap gap-4 mt-4 p-3 rounded-3"
                style={{ background: "#f1f5f9" }}
              >
                <div className="d-flex align-items-center gap-2 text-danger fw-bold">
                  <CalendarDays size={20} />
                  <span>يتم غلق التسجيل في: {militaryRegistrationEndDate}</span>
                </div>
                <div className="d-flex align-items-center gap-2 text-primary fw-bold">
                  <Wallet size={20} />
                  <span>الرسوم: عبر المطالبة المالية</span>
                </div>
              </div>
            </motion.div>

            {/* Status Messages */}
            {regStatus === "PENDING" && (
              <div className="alert alert-warning d-flex align-items-center mb-4 border-0 bg-warning bg-opacity-10 text-warning rounded-4 p-4 shadow-sm">
                <div>
                  <h5 className="fw-bold mb-2">طلبك قيد المراجعة حالياً</h5>
                  <p className="m-0 text-dark opacity-75">
                    تم استلام طلبك بنجاح وجاري مراجعته من قبل إدارة رعاية
                    الشباب. يمكنك تعديل بياناتك أو صورك المرفقة قبل الاعتماد
                    النهائي.
                  </p>
                </div>
              </div>
            )}

            {regStatus === "REJECTED" && (
              <div className="alert alert-danger d-flex align-items-start mb-4 border-0 bg-danger bg-opacity-10 text-danger rounded-4 p-4 shadow-sm">
                <AlertCircle size={32} className="ms-3 flex-shrink-0" />
                <div>
                  <h5 className="fw-bold mb-2">تم رفض الطلب</h5>
                  <p className="m-0 fw-semibold">
                    سبب الرفض:{" "}
                    {adminNotes ||
                      "يرجى مراجعة بياناتك وإعادة رفع صور واضحة للأوراق المطلوبة."}
                  </p>
                </div>
              </div>
            )}

            {/* Main Content Area */}
            {regStatus === "APPROVED" ? (
              <motion.div
                className="success-state text-center py-5 glass-card"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
              >
                <CheckCircle2 size={80} className="text-success mb-4 mx-auto" />
                <h3 className="fw-bold mb-3" style={{ color: "#0f172a" }}>
                  تم قبول تسجيلك بنجاح!
                </h3>
                <p className="text-secondary fw-medium fs-5">
                  تم مراجعة أوراقك واعتماد تسجيلك في {courseName} من قبل
                  الإدارة. نتمنى لك التوفيق.
                </p>

                {groupLink && (
                  <div className="alert alert-info bg-info bg-opacity-10 text-info border-0 rounded-3 mb-4 fw-semibold">
                    <Info size={18} className="ms-2 d-inline" />
                    رابط المجموعة:{" "}
                    <a
                      href={groupLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-underline"
                    >
                      انقر هنا
                    </a>
                  </div>
                )}
              </motion.div>
            ) : !isRegistrationOpen && regStatus === "NOT_REGISTERED" ? (
              <motion.div
                className="closed-state text-center py-5 glass-card border-danger border-opacity-25"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
              >
                <Lock
                  size={80}
                  className="text-danger mb-4 mx-auto opacity-75"
                />
                <h3 className="fw-bold mb-3" style={{ color: "#0f172a" }}>
                  باب التسجيل مغلق
                </h3>
                <p className="text-secondary fw-medium fs-5">
                  عفواً، فترة التسجيل في {courseName} مغلقة حالياً. يرجى متابعة
                  إعلانات الإدارة لمعرفة موعد فتح التسجيل القادم.
                </p>
              </motion.div>
            ) : (
              <motion.div
                className="form-card glass-card"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <form onSubmit={handleSubmit}>
                  <h4 className="mb-4 fw-bold" style={{ color: "#0f172a" }}>
                    {regStatus === "NOT_REGISTERED"
                      ? "استمارة حجز الدورة"
                      : "تحديث بيانات التسجيل"}
                  </h4>

                  {!isRegistrationOpen && regStatus !== "NOT_REGISTERED" && (
                    <div className="alert alert-info bg-info bg-opacity-10 text-info border-0 rounded-3 mb-4 fw-semibold">
                      <Info size={18} className="ms-2 d-inline" />
                      التسجيل الجديد مغلق، ولكن لا يزال بإمكانك تحديث بيانات
                      طلبك الحالي.
                    </div>
                  )}

                  {submitMessage && (
                    <div
                      className={`alert ${submitMessage.type === "success" ? "alert-success bg-success text-success" : "alert-danger bg-danger text-danger"} bg-opacity-10 border-0 rounded-3 mb-4 fw-bold`}
                    >
                      {submitMessage.text}
                    </div>
                  )}

                  {/* الصف الأول: الاسم والرقم القومي */}
                  <div className="row g-4 mb-4">
                    <div className="col-12 col-md-6">
                      <div className="custom-input-group">
                        <label className="form-label text-secondary fw-bold">
                          الاسم الرباعي (كما ببطاقة الرقم القومي)
                        </label>
                        <div className="input-wrapper">
                          <User size={20} className="input-icon" />
                          <input
                            type="text"
                            className="custom-input text-end"
                            placeholder="أدخل اسمك الرباعي باللغة العربية"
                            required
                            value={formData.fullName}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                fullName: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="custom-input-group">
                        <label className="form-label text-secondary fw-bold">
                          الرقم القومي (14 رقم)
                        </label>
                        <div className="input-wrapper">
                          <CreditCard size={20} className="input-icon" />
                          <input
                            type="text"
                            className="custom-input text-end"
                            placeholder="أدخل الرقم القومي الخاص بك"
                            required
                            minLength={14}
                            maxLength={14}
                            value={formData.nationalIdNumber}
                            onChange={handleNationalIdChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 🆕 الصف الثاني: الإيميل الجامعي ومقاس التيشيرت */}
                  <div className="row g-4 mb-4">
                    <div className="col-12 col-md-8">
                      <div className="custom-input-group">
                        <label className="form-label text-secondary fw-bold">
                          البريد الإلكتروني الجامعي
                        </label>
                        <div className="input-wrapper">
                          <Mail size={20} className="input-icon" />
                          <input
                            type="email"
                            className="custom-input text-end"
                            placeholder="مثال: a.hmada2698@nub.edu.eg"
                            required
                            value={formData.universityEmail}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                universityEmail: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div className="custom-input-group">
                        <label className="form-label text-secondary fw-bold">
                          مقاس التيشيرت
                        </label>
                        <div className="input-wrapper">
                          <Shirt size={20} className="input-icon" />
                          {/* استخدام select للخيارات المحددة */}
                          <select
                            className="custom-input text-end form-select"
                            style={{ paddingRight: "40px" }}
                            required
                            value={formData.tShirtSize}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                tShirtSize: e.target.value,
                              })
                            }
                          >
                            <option value="" disabled>
                              اختر المقاس
                            </option>
                            <option value="S">S</option>
                            <option value="M">M</option>
                            <option value="L">L</option>
                            <option value="XL">XL</option>
                            <option value="XXL">XXL</option>
                            <option value="3XL">3XL</option>
                            <option value="4XL">4XL</option>
                            <option value="5XL">5XL</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h5
                    className="fw-bold mt-5 mb-4"
                    style={{ color: "#334155" }}
                  >
                    الأوراق المطلوبة للرفع
                  </h5>

                  <div className="row g-4 mb-5">
                    {/* الصورة الشخصية */}
                    <div className="col-12 col-md-3 col-sm-6">
                      <label className="form-label text-secondary fw-bold small">
                        صورة شخصية للطالب
                      </label>
                      <label
                        className={`upload-zone p-3 ${formData.personalPhoto || existingImages.personalPhoto ? "has-file" : ""}`}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="d-none"
                          required={regStatus === "NOT_REGISTERED"}
                          onChange={(e) => handleFileChange(e, "personalPhoto")}
                        />
                        <div className="upload-content text-center">
                          {formData.personalPhoto ? (
                            <>
                              <img
                                src={URL.createObjectURL(
                                  formData.personalPhoto,
                                )}
                                alt="Personal Preview"
                                className="file-preview-img mb-2"
                              />
                              <span className="file-name text-warning small d-block">
                                تم إرفاق صورة جديدة
                              </span>
                            </>
                          ) : existingImages.personalPhoto ? (
                            <>
                              <img
                                src={existingImages.personalPhoto}
                                alt="Existing Personal"
                                className="file-preview-img mb-2"
                              />
                              <span className="file-name text-success small d-block">
                                الحالية (اضغط للتغيير)
                              </span>
                            </>
                          ) : (
                            <>
                              <User
                                size={32}
                                className="text-primary mb-2 mx-auto"
                              />
                              <span className="small fw-semibold mt-2">
                                اختر ملف
                              </span>
                            </>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* صورة كارنيه الجامعة */}
                    <div className="col-12 col-md-3 col-sm-6">
                      <label className="form-label text-secondary fw-bold small">
                        صورة كارنيه الجامعة
                      </label>
                      <label
                        className={`upload-zone p-3 ${formData.universityId || existingImages.universityId ? "has-file" : ""}`}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="d-none"
                          required={regStatus === "NOT_REGISTERED"}
                          onChange={(e) => handleFileChange(e, "universityId")}
                        />
                        <div className="upload-content text-center">
                          {formData.universityId ? (
                            <>
                              <img
                                src={URL.createObjectURL(formData.universityId)}
                                alt="Uni ID Preview"
                                className="file-preview-img mb-2"
                              />
                              <span className="file-name text-warning small d-block">
                                تم إرفاق صورة جديدة
                              </span>
                            </>
                          ) : existingImages.universityId ? (
                            <>
                              <img
                                src={existingImages.universityId}
                                alt="Existing Uni ID"
                                className="file-preview-img mb-2"
                              />
                              <span className="file-name text-success small d-block">
                                الحالية (اضغط للتغيير)
                              </span>
                            </>
                          ) : (
                            <>
                              <FileText
                                size={32}
                                className="text-primary mb-2 mx-auto"
                              />
                              <span className="small fw-semibold mt-2">
                                اختر ملف
                              </span>
                            </>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* وش البطاقة */}
                    <div className="col-12 col-md-3 col-sm-6">
                      <label className="form-label text-secondary fw-bold small">
                        بطاقة الرقم القومي (الوجه)
                      </label>
                      <label
                        className={`upload-zone p-3 ${formData.nationalIdFront || existingImages.nationalIdFront ? "has-file" : ""}`}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="d-none"
                          required={regStatus === "NOT_REGISTERED"}
                          onChange={(e) =>
                            handleFileChange(e, "nationalIdFront")
                          }
                        />
                        <div className="upload-content text-center">
                          {formData.nationalIdFront ? (
                            <>
                              <img
                                src={URL.createObjectURL(
                                  formData.nationalIdFront,
                                )}
                                alt="Front Preview"
                                className="file-preview-img mb-2"
                              />
                              <span className="file-name text-warning small d-block">
                                تم إرفاق صورة جديدة
                              </span>
                            </>
                          ) : existingImages.nationalIdFront ? (
                            <>
                              <img
                                src={existingImages.nationalIdFront}
                                alt="Existing Front"
                                className="file-preview-img mb-2"
                              />
                              <span className="file-name text-success small d-block">
                                الحالية (اضغط للتغيير)
                              </span>
                            </>
                          ) : (
                            <>
                              <ImageIcon
                                size={32}
                                className="text-primary mb-2 mx-auto"
                              />
                              <span className="small fw-semibold mt-2">
                                اختر ملف
                              </span>
                            </>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* ظهر البطاقة */}
                    <div className="col-12 col-md-3 col-sm-6">
                      <label className="form-label text-secondary fw-bold small">
                        بطاقة الرقم القومي (الظهر)
                      </label>
                      <label
                        className={`upload-zone p-3 ${formData.nationalIdBack || existingImages.nationalIdBack ? "has-file" : ""}`}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="d-none"
                          required={regStatus === "NOT_REGISTERED"}
                          onChange={(e) =>
                            handleFileChange(e, "nationalIdBack")
                          }
                        />
                        <div className="upload-content text-center">
                          {formData.nationalIdBack ? (
                            <>
                              <img
                                src={URL.createObjectURL(
                                  formData.nationalIdBack,
                                )}
                                alt="Back Preview"
                                className="file-preview-img mb-2"
                              />
                              <span className="file-name text-warning small d-block">
                                تم إرفاق صورة جديدة
                              </span>
                            </>
                          ) : existingImages.nationalIdBack ? (
                            <>
                              <img
                                src={existingImages.nationalIdBack}
                                alt="Existing Back"
                                className="file-preview-img mb-2"
                              />
                              <span className="file-name text-success small d-block">
                                الحالية (اضغط للتغيير)
                              </span>
                            </>
                          ) : (
                            <>
                              <ImageIcon
                                size={32}
                                className="text-primary mb-2 mx-auto"
                              />
                              <span className="small fw-semibold mt-2">
                                اختر ملف
                              </span>
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn-submit w-100 d-flex justify-content-center align-items-center gap-3 mt-2"
                    disabled={
                      isSubmitting ||
                      (regStatus !== "NOT_REGISTERED" && !hasChanges)
                    }
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2
                          size={24}
                          className="spinner-icon text-white"
                        />
                        <span>جاري حفظ البيانات...</span>
                      </>
                    ) : regStatus === "NOT_REGISTERED" ? (
                      <span className="fs-5">تأكيد التسجيل في الدورة</span>
                    ) : (
                      <span className="fs-5">حفظ التعديلات</span>
                    )}
                  </button>
                </form>
              </motion.div>
            )}
          </div>
        </div>
      </div>
      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ ...popup, show: false })}
        />
      )}
    </div>
  );
}

// import React, { useState, useContext, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import { motion } from "framer-motion";
// import {
//   ArrowRight,
//   Shield,
//   Info,
//   CheckCircle2,
//   FileText,
//   User,
//   Loader2,
//   AlertCircle,
//   CreditCard,
//   Image as ImageIcon,
//   Lock,
//   CalendarDays,
//   Wallet,
// } from "lucide-react";
// import "../../assets/styles/MilitaryEducation.css";
// import { AuthContext } from "../../context/AuthContext";
// import api from "../../services/api";
// import PopupMessage from "../../components/common/PopupMessage";

// export default function MilitaryEducation() {
//   const navigate = useNavigate();
//   const { user } = useContext(AuthContext);
//   const isMale = user?.gender?.toUpperCase() === "MALE";

//   // تغيير المسمى بناءً على الجنس زي الإعلان الرسمي
//   const courseName = isMale ? "دورة التربية العسكرية" : "دورة التربية الوطنية";

//   const [isLoadingData, setIsLoadingData] = useState(true);
//   const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
//   const [militaryRegistrationEndDate, setMilitaryRegistrationEndDate] =
//     useState("");
//   const [regStatus, setRegStatus] = useState("NOT_REGISTERED");
//   const [adminNotes, setAdminNotes] = useState("");

//   const [originalData, setOriginalData] = useState({
//     fullName: "",
//     nationalIdNumber: "",
//   });

//   const [existingImages, setExistingImages] = useState({
//     personalPhoto: null,
//     nationalIdFront: null,
//     nationalIdBack: null,
//     universityId: null,
//   });

//   const [formData, setFormData] = useState({
//     fullName: "",
//     nationalIdNumber: "",
//     personalPhoto: null,
//     nationalIdFront: null,
//     nationalIdBack: null,
//     universityId: null,
//   });

//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [submitMessage, setSubmitMessage] = useState(null);
//   const [popup, setPopup] = useState({ show: false, type: "", message: "" });

//   // دالة جلب البيانات (مفصولة عشان نقدر ننادي عليها في أي وقت بدون ريفريش)
//   const fetchMilitaryData = useCallback(
//     async (isSilent = false) => {
//       if (!isSilent) setIsLoadingData(true);
//       try {
//         const [settingsRes, statusRes] = await Promise.all([
//           api
//             .get("/Military/settings")
//             .catch(() => ({ data: { isMilitaryRegistrationOpen: true } })),
//           api.get("/Military/status"),
//         ]);

//         setIsRegistrationOpen(settingsRes.data.isMilitaryRegistrationOpen);
//         if (settingsRes.data.militaryRegistrationEndDate) {
//           setMilitaryRegistrationEndDate(
//             settingsRes.data.militaryRegistrationEndDate.split("T")[0],
//           );
//         }

//         if (statusRes.data.isRegistered) {
//           const { status, adminNotes, data } = statusRes.data;
//           setRegStatus(status);
//           setAdminNotes(adminNotes);

//           const loadedData = {
//             fullName: data.fullName,
//             nationalIdNumber: data.nationalIdNumber,
//           };

//           // تحديث البيانات الأصلية وبيانات الفورم وتفريغ الصور المختارة
//           setOriginalData(loadedData);
//           setFormData((prev) => ({
//             ...prev,
//             ...loadedData,
//             personalPhoto: null,
//             nationalIdFront: null,
//             nationalIdBack: null,
//             universityId: null,
//           }));

//           setExistingImages({
//             personalPhoto: data.personalPhoto?.secure_url,
//             nationalIdFront: data.nationalIdFrontImage?.secure_url,
//             nationalIdBack: data.nationalIdBackImage?.secure_url,
//             universityId: data.universityIdImage?.secure_url,
//           });
//         } else {
//           if (user?.name) {
//             setFormData((prev) => ({ ...prev, fullName: user.name }));
//             setOriginalData((prev) => ({ ...prev, fullName: user.name }));
//           }
//         }
//       } catch (error) {
//         console.error("Error fetching data:", error);
//       } finally {
//         if (!isSilent) setIsLoadingData(false);
//       }
//     },
//     [user],
//   );

//   useEffect(() => {
//     fetchMilitaryData();
//   }, [fetchMilitaryData]);

//   const hasChanges =
//     formData.fullName !== originalData.fullName ||
//     formData.nationalIdNumber !== originalData.nationalIdNumber ||
//     formData.personalPhoto !== null ||
//     formData.nationalIdFront !== null ||
//     formData.nationalIdBack !== null ||
//     formData.universityId !== null;

//   const guidelines = [
//     `تقام الدورة بنظام الحضور الفعلي بمقر الجامعة لمدة 12 يوماً خلال إجازة نهاية العام.`,
//     isMale
//       ? "الدورة متطلب تخرج أساسي للطلاب الذكور."
//       : "الدورة عبارة عن محاضرات تثقيفية فقط كمتطلب تخرج للطالبات.",
//     "يجب تسليم الأوراق شخصياً بإدارة رعاية الشباب بمبنى الأنشطة بعد إتمام التسجيل الإلكتروني.",
//     "سيتم تحميل رسوم الدورة من خلال مطالبة مالية خاصة بذلك.",
//     "لا يُقبل الاعتذار عن الدورة بعد التسجيل نهائياً.",
//   ];

//   const handleFileChange = (e, field) => {
//     const file = e.target.files[0];
//     if (file) {
//       const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

//       if (file.size > maxSizeInBytes) {
//         setPopup({
//           show: true,
//           type: "error",
//           message: "حجم الصورة كبير جداً. أقصى حجم مسموح به هو 5 ميجابايت.",
//         });
//         e.target.value = null;
//         return;
//       }

//       setFormData({ ...formData, [field]: file });
//     }
//   };

//   const handleNationalIdChange = (e) => {
//     const value = e.target.value.replace(/\D/g, "");
//     if (value.length <= 14) {
//       setFormData({ ...formData, nationalIdNumber: value });
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (formData.nationalIdNumber.length !== 14) {
//       setSubmitMessage({
//         type: "error",
//         text: "الرقم القومي يجب أن يتكون من 14 رقم بالضبط.",
//       });
//       setPopup({
//         show: true,
//         type: "error",
//         message: "الرقم القومي يجب أن يتكون من 14 رقم بالضبط.",
//       });
//       return;
//     }

//     if (regStatus === "NOT_REGISTERED" && !formData.personalPhoto) {
//       setPopup({
//         show: true,
//         type: "error",
//         message: "يرجى رفع الصورة الشخصية.",
//       });
//       return;
//     }

//     setIsSubmitting(true);
//     setSubmitMessage(null);

//     const submitData = new FormData();
//     submitData.append("fullName", formData.fullName);
//     submitData.append("nationalIdNumber", formData.nationalIdNumber);

//     if (formData.personalPhoto)
//       submitData.append("personalPhoto", formData.personalPhoto);
//     if (formData.nationalIdFront)
//       submitData.append("nationalIdFrontImage", formData.nationalIdFront);
//     if (formData.nationalIdBack)
//       submitData.append("nationalIdBackImage", formData.nationalIdBack);
//     if (formData.universityId)
//       submitData.append("universityIdImage", formData.universityId);

//     try {
//       if (regStatus === "NOT_REGISTERED") {
//         await api.post("/Military/register", submitData);
//       } else {
//         await api.patch("/Military/update", submitData);
//       }

//       setSubmitMessage({ type: "success", text: "تم حفظ بياناتك بنجاح!" });
//       setPopup({
//         show: true,
//         type: "success",
//         message: "تم إرسال طلبك بنجاح، يرجى متابعة حالة الطلب من هنا.",
//       });

//       // جلب البيانات الجديدة من السيرفر بدون ريفريش للصفحة
//       await fetchMilitaryData(true);

//       setTimeout(() => {
//         setSubmitMessage(null);
//         window.scrollTo({ top: 0, behavior: "smooth" });
//       }, 2000);
//     } catch (error) {
//       let errorMessage = "حدث خطأ أثناء إرسال البيانات.";

//       if (error.response?.data?.errors) {
//         const validationErrors = error.response.data.errors;
//         const firstErrorKey = Object.keys(validationErrors)[0];
//         if (firstErrorKey && validationErrors[firstErrorKey].length > 0) {
//           errorMessage = validationErrors[firstErrorKey][0];
//         }
//       } else if (error.response?.data?.message) {
//         errorMessage = error.response.data.message;
//       }

//       setSubmitMessage({
//         type: "error",
//         text: errorMessage,
//       });
//       setPopup({
//         show: true,
//         type: "error",
//         message: errorMessage,
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   if (isLoadingData) {
//     return (
//       <div className="military-page-wrapper d-flex justify-content-center align-items-center">
//         <Loader2 className="spinner-icon text-primary" size={48} />
//       </div>
//     );
//   }

//   return (
//     <div className="military-page-wrapper" dir="rtl">
//       <div
//         style={{ marginTop: "50px" }}
//         className="container position-relative z-2"
//       >
//         {/* Header */}
//         <div className="d-flex align-items-center mb-5">
//           <button
//             onClick={() => navigate(-1)}
//             className="btn btn-link p-0 ms-3 text-secondary"
//             style={{ textDecoration: "none" }}
//           >
//             <ArrowRight size={28} />
//           </button>
//           <div className="d-flex align-items-center gap-3">
//             <Shield size={36} className="text-primary" />
//             <h2
//               className="m-0 fw-bold title-gradient"
//               style={{ fontSize: "2rem" }}
//             >
//               حجز {courseName}
//             </h2>
//           </div>
//         </div>

//         <div className="row justify-content-center">
//           <div className="col-12 col-lg-10">
//             {/* Guidelines Card */}
//             <motion.div
//               className="info-card glass-card mb-4"
//               initial={{ y: 20, opacity: 0 }}
//               animate={{ y: 0, opacity: 1 }}
//               transition={{ duration: 0.5 }}
//             >
//               <div className="d-flex align-items-center gap-2 mb-4 pb-3 border-bottom border-secondary border-opacity-25">
//                 <Info size={26} className="text-primary" />
//                 <h4 className="m-0 fw-bold" style={{ color: "#1e293b" }}>
//                   تعليمات الإدارة للتسجيل بالدورة
//                 </h4>
//               </div>
//               <ul className="guidelines-list mb-4">
//                 {guidelines.map((item, index) => (
//                   <motion.li
//                     key={index}
//                     initial={{ x: 10, opacity: 0 }}
//                     animate={{ x: 0, opacity: 1 }}
//                     transition={{ delay: index * 0.1 + 0.3 }}
//                   >
//                     <CheckCircle2
//                       size={20}
//                       className="text-success ms-3 flex-shrink-0 mt-1"
//                     />
//                     <span className="fw-semibold text-secondary">{item}</span>
//                   </motion.li>
//                 ))}
//               </ul>
//               <div
//                 className="d-flex flex-wrap gap-4 mt-4 p-3 rounded-3"
//                 style={{ background: "#f1f5f9" }}
//               >
//                 <div className="d-flex align-items-center gap-2 text-danger fw-bold">
//                   <CalendarDays size={20} />
//                   <span>يتم غلق التسجيل في: {militaryRegistrationEndDate}</span>
//                 </div>
//                 <div className="d-flex align-items-center gap-2 text-primary fw-bold">
//                   <Wallet size={20} />
//                   <span>الرسوم: عبر المطالبة المالية</span>
//                 </div>
//               </div>
//             </motion.div>

//             {/* Status Messages */}
//             {regStatus === "PENDING" && (
//               <div className="alert alert-warning d-flex align-items-center mb-4 border-0 bg-warning bg-opacity-10 text-warning rounded-4 p-4 shadow-sm">
//                 <div>
//                   <h5 className="fw-bold mb-2">طلبك قيد المراجعة حالياً</h5>
//                   <p className="m-0 text-dark opacity-75">
//                     تم استلام طلبك بنجاح وجاري مراجعته من قبل إدارة رعاية
//                     الشباب. يمكنك تعديل بياناتك أو صورك المرفقة قبل الاعتماد
//                     النهائي.
//                   </p>
//                 </div>
//               </div>
//             )}

//             {regStatus === "REJECTED" && (
//               <div className="alert alert-danger d-flex align-items-start mb-4 border-0 bg-danger bg-opacity-10 text-danger rounded-4 p-4 shadow-sm">
//                 <AlertCircle size={32} className="ms-3 flex-shrink-0" />
//                 <div>
//                   <h5 className="fw-bold mb-2">تم رفض الطلب</h5>
//                   <p className="m-0 fw-semibold">
//                     سبب الرفض:{" "}
//                     {adminNotes ||
//                       "يرجى مراجعة بياناتك وإعادة رفع صور واضحة للأوراق المطلوبة."}
//                   </p>
//                 </div>
//               </div>
//             )}

//             {/* Main Content Area */}
//             {regStatus === "APPROVED" ? (
//               <motion.div
//                 className="success-state text-center py-5 glass-card"
//                 initial={{ scale: 0.9 }}
//                 animate={{ scale: 1 }}
//               >
//                 <CheckCircle2 size={80} className="text-success mb-4 mx-auto" />
//                 <h3 className="fw-bold mb-3" style={{ color: "#0f172a" }}>
//                   تم قبول تسجيلك بنجاح!
//                 </h3>
//                 <p className="text-secondary fw-medium fs-5">
//                   تم مراجعة أوراقك واعتماد تسجيلك في {courseName} من قبل
//                   الإدارة. نتمنى لك التوفيق.
//                 </p>
//               </motion.div>
//             ) : !isRegistrationOpen && regStatus === "NOT_REGISTERED" ? (
//               <motion.div
//                 className="closed-state text-center py-5 glass-card border-danger border-opacity-25"
//                 initial={{ scale: 0.9, opacity: 0 }}
//                 animate={{ scale: 1, opacity: 1 }}
//               >
//                 <Lock
//                   size={80}
//                   className="text-danger mb-4 mx-auto opacity-75"
//                 />
//                 <h3 className="fw-bold mb-3" style={{ color: "#0f172a" }}>
//                   باب التسجيل مغلق
//                 </h3>
//                 <p className="text-secondary fw-medium fs-5">
//                   عفواً، فترة التسجيل في {courseName} مغلقة حالياً. يرجى متابعة
//                   إعلانات الإدارة لمعرفة موعد فتح التسجيل القادم.
//                 </p>
//               </motion.div>
//             ) : (
//               <motion.div
//                 className="form-card glass-card"
//                 initial={{ y: 20, opacity: 0 }}
//                 animate={{ y: 0, opacity: 1 }}
//                 transition={{ duration: 0.5, delay: 0.2 }}
//               >
//                 <form onSubmit={handleSubmit}>
//                   <h4 className="mb-4 fw-bold" style={{ color: "#0f172a" }}>
//                     {regStatus === "NOT_REGISTERED"
//                       ? "استمارة حجز الدورة"
//                       : "تحديث بيانات التسجيل"}
//                   </h4>

//                   {!isRegistrationOpen && regStatus !== "NOT_REGISTERED" && (
//                     <div className="alert alert-info bg-info bg-opacity-10 text-info border-0 rounded-3 mb-4 fw-semibold">
//                       <Info size={18} className="ms-2 d-inline" />
//                       التسجيل الجديد مغلق، ولكن لا يزال بإمكانك تحديث بيانات
//                       طلبك الحالي.
//                     </div>
//                   )}

//                   {submitMessage && (
//                     <div
//                       className={`alert ${submitMessage.type === "success" ? "alert-success bg-success text-success" : "alert-danger bg-danger text-danger"} bg-opacity-10 border-0 rounded-3 mb-4 fw-bold`}
//                     >
//                       {submitMessage.text}
//                     </div>
//                   )}

//                   <div className="row g-4 mb-4">
//                     <div className="col-12 col-md-6">
//                       <div className="custom-input-group">
//                         <label className="form-label text-secondary fw-bold">
//                           الاسم الرباعي (كما ببطاقة الرقم القومي)
//                         </label>
//                         <div className="input-wrapper">
//                           <User size={20} className="input-icon" />
//                           <input
//                             type="text"
//                             className="custom-input text-end"
//                             placeholder="أدخل اسمك الرباعي باللغة العربية"
//                             required
//                             value={formData.fullName}
//                             onChange={(e) =>
//                               setFormData({
//                                 ...formData,
//                                 fullName: e.target.value,
//                               })
//                             }
//                           />
//                         </div>
//                       </div>
//                     </div>

//                     <div className="col-12 col-md-6">
//                       <div className="custom-input-group">
//                         <label className="form-label text-secondary fw-bold">
//                           الرقم القومي (14 رقم)
//                         </label>
//                         <div className="input-wrapper">
//                           <CreditCard size={20} className="input-icon" />
//                           <input
//                             type="text"
//                             className="custom-input text-end"
//                             placeholder="أدخل الرقم القومي الخاص بك"
//                             required
//                             minLength={14}
//                             maxLength={14}
//                             value={formData.nationalIdNumber}
//                             onChange={handleNationalIdChange}
//                           />
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   <h5
//                     className="fw-bold mt-5 mb-4"
//                     style={{ color: "#334155" }}
//                   >
//                     الأوراق المطلوبة للرفع
//                   </h5>

//                   <div className="row g-4 mb-5">
//                     {/* الصورة الشخصية */}
//                     <div className="col-12 col-md-3 col-sm-6">
//                       <label className="form-label text-secondary fw-bold small">
//                         صورة شخصية للطالب
//                       </label>
//                       <label
//                         className={`upload-zone p-3 ${formData.personalPhoto || existingImages.personalPhoto ? "has-file" : ""}`}
//                       >
//                         <input
//                           type="file"
//                           accept="image/*"
//                           className="d-none"
//                           required={regStatus === "NOT_REGISTERED"}
//                           onChange={(e) => handleFileChange(e, "personalPhoto")}
//                         />
//                         <div className="upload-content text-center">
//                           {formData.personalPhoto ? (
//                             <>
//                               <img
//                                 src={URL.createObjectURL(
//                                   formData.personalPhoto,
//                                 )}
//                                 alt="Personal Preview"
//                                 className="file-preview-img mb-2"
//                               />
//                               <span className="file-name text-warning small d-block">
//                                 تم إرفاق صورة جديدة
//                               </span>
//                             </>
//                           ) : existingImages.personalPhoto ? (
//                             <>
//                               <img
//                                 src={existingImages.personalPhoto}
//                                 alt="Existing Personal"
//                                 className="file-preview-img mb-2"
//                               />
//                               <span className="file-name text-success small d-block">
//                                 الحالية (اضغط للتغيير)
//                               </span>
//                             </>
//                           ) : (
//                             <>
//                               <User
//                                 size={32}
//                                 className="text-primary mb-2 mx-auto"
//                               />
//                               <span className="small fw-semibold mt-2">
//                                 اختر ملف
//                               </span>
//                             </>
//                           )}
//                         </div>
//                       </label>
//                     </div>

//                     {/* صورة كارنيه الجامعة */}
//                     <div className="col-12 col-md-3 col-sm-6">
//                       <label className="form-label text-secondary fw-bold small">
//                         صورة كارنيه الجامعة
//                       </label>
//                       <label
//                         className={`upload-zone p-3 ${formData.universityId || existingImages.universityId ? "has-file" : ""}`}
//                       >
//                         <input
//                           type="file"
//                           accept="image/*"
//                           className="d-none"
//                           required={regStatus === "NOT_REGISTERED"}
//                           onChange={(e) => handleFileChange(e, "universityId")}
//                         />
//                         <div className="upload-content text-center">
//                           {formData.universityId ? (
//                             <>
//                               <img
//                                 src={URL.createObjectURL(formData.universityId)}
//                                 alt="Uni ID Preview"
//                                 className="file-preview-img mb-2"
//                               />
//                               <span className="file-name text-warning small d-block">
//                                 تم إرفاق صورة جديدة
//                               </span>
//                             </>
//                           ) : existingImages.universityId ? (
//                             <>
//                               <img
//                                 src={existingImages.universityId}
//                                 alt="Existing Uni ID"
//                                 className="file-preview-img mb-2"
//                               />
//                               <span className="file-name text-success small d-block">
//                                 الحالية (اضغط للتغيير)
//                               </span>
//                             </>
//                           ) : (
//                             <>
//                               <FileText
//                                 size={32}
//                                 className="text-primary mb-2 mx-auto"
//                               />
//                               <span className="small fw-semibold mt-2">
//                                 اختر ملف
//                               </span>
//                             </>
//                           )}
//                         </div>
//                       </label>
//                     </div>

//                     {/* وش البطاقة */}
//                     <div className="col-12 col-md-3 col-sm-6">
//                       <label className="form-label text-secondary fw-bold small">
//                         بطاقة الرقم القومي (الوجه)
//                       </label>
//                       <label
//                         className={`upload-zone p-3 ${formData.nationalIdFront || existingImages.nationalIdFront ? "has-file" : ""}`}
//                       >
//                         <input
//                           type="file"
//                           accept="image/*"
//                           className="d-none"
//                           required={regStatus === "NOT_REGISTERED"}
//                           onChange={(e) =>
//                             handleFileChange(e, "nationalIdFront")
//                           }
//                         />
//                         <div className="upload-content text-center">
//                           {formData.nationalIdFront ? (
//                             <>
//                               <img
//                                 src={URL.createObjectURL(
//                                   formData.nationalIdFront,
//                                 )}
//                                 alt="Front Preview"
//                                 className="file-preview-img mb-2"
//                               />
//                               <span className="file-name text-warning small d-block">
//                                 تم إرفاق صورة جديدة
//                               </span>
//                             </>
//                           ) : existingImages.nationalIdFront ? (
//                             <>
//                               <img
//                                 src={existingImages.nationalIdFront}
//                                 alt="Existing Front"
//                                 className="file-preview-img mb-2"
//                               />
//                               <span className="file-name text-success small d-block">
//                                 الحالية (اضغط للتغيير)
//                               </span>
//                             </>
//                           ) : (
//                             <>
//                               <ImageIcon
//                                 size={32}
//                                 className="text-primary mb-2 mx-auto"
//                               />
//                               <span className="small fw-semibold mt-2">
//                                 اختر ملف
//                               </span>
//                             </>
//                           )}
//                         </div>
//                       </label>
//                     </div>

//                     {/* ظهر البطاقة */}
//                     <div className="col-12 col-md-3 col-sm-6">
//                       <label className="form-label text-secondary fw-bold small">
//                         بطاقة الرقم القومي (الظهر)
//                       </label>
//                       <label
//                         className={`upload-zone p-3 ${formData.nationalIdBack || existingImages.nationalIdBack ? "has-file" : ""}`}
//                       >
//                         <input
//                           type="file"
//                           accept="image/*"
//                           className="d-none"
//                           required={regStatus === "NOT_REGISTERED"}
//                           onChange={(e) =>
//                             handleFileChange(e, "nationalIdBack")
//                           }
//                         />
//                         <div className="upload-content text-center">
//                           {formData.nationalIdBack ? (
//                             <>
//                               <img
//                                 src={URL.createObjectURL(
//                                   formData.nationalIdBack,
//                                 )}
//                                 alt="Back Preview"
//                                 className="file-preview-img mb-2"
//                               />
//                               <span className="file-name text-warning small d-block">
//                                 تم إرفاق صورة جديدة
//                               </span>
//                             </>
//                           ) : existingImages.nationalIdBack ? (
//                             <>
//                               <img
//                                 src={existingImages.nationalIdBack}
//                                 alt="Existing Back"
//                                 className="file-preview-img mb-2"
//                               />
//                               <span className="file-name text-success small d-block">
//                                 الحالية (اضغط للتغيير)
//                               </span>
//                             </>
//                           ) : (
//                             <>
//                               <ImageIcon
//                                 size={32}
//                                 className="text-primary mb-2 mx-auto"
//                               />
//                               <span className="small fw-semibold mt-2">
//                                 اختر ملف
//                               </span>
//                             </>
//                           )}
//                         </div>
//                       </label>
//                     </div>
//                   </div>

//                   <button
//                     type="submit"
//                     className="btn-submit w-100 d-flex justify-content-center align-items-center gap-3 mt-2"
//                     disabled={
//                       isSubmitting ||
//                       (regStatus !== "NOT_REGISTERED" && !hasChanges)
//                     }
//                   >
//                     {isSubmitting ? (
//                       <>
//                         <Loader2
//                           size={24}
//                           className="spinner-icon text-white"
//                         />
//                         <span>جاري حفظ البيانات...</span>
//                       </>
//                     ) : regStatus === "NOT_REGISTERED" ? (
//                       <span className="fs-5">تأكيد التسجيل في الدورة</span>
//                     ) : (
//                       <span className="fs-5">حفظ التعديلات</span>
//                     )}
//                   </button>
//                 </form>
//               </motion.div>
//             )}
//           </div>
//         </div>
//       </div>
//       {popup.show && (
//         <PopupMessage
//           type={popup.type}
//           message={popup.message}
//           onClose={() => setPopup({ ...popup, show: false })}
//         />
//       )}
//     </div>
//   );
// }
